<template>
  <div class="header--wrapper">
    <b-tooltip
      v-if="folder.description"
      target="folder__title"
      :title="folder.description"
    />
    <h5
      id="folder__title"
      class="header--wrapper__title"
    >
      <font-awesome-icon
        :icon="['fas', folder.icon || 'folder']"
        class="mr-2"
      />
      {{ folder.label }}
    </h5>
    <div class="actions--container">
      <b-dropdown
        v-if="canCreate(folder) && !isLoanCalculatorModule"
        :id="`${idTemplate}-createNewTable`"
        size="sm"
        :disabled="isDragModeEnabled"
        variant="outline-secondary"
        class="connected-btn"
      >
        <template #button-content>
          <font-awesome-icon :icon="['fas', 'plus']" />
          Create new Table
        </template>
        <b-dropdown-item
          :id="`${idTemplate}-createNewTable-mdm`"
          @click="createTable(false)"
        >
          MDM
        </b-dropdown-item>
        <b-dropdown-item @click="createTable(true)">
          Power Apps
        </b-dropdown-item>
        <b-dropdown-item @click="createTable(false, true)">
          Power Bi Report
        </b-dropdown-item>
      </b-dropdown>
      <b-button
        v-if="canEdit(folder) && !isLoanCalculatorModule"
        :id="`${idTemplate}-editFolder`"
        size="sm"
        variant="outline-primary"
        class="btn-folder-edit"
        :disabled="isDragModeEnabled"
        @click="showEditFolderModal"
      >
        <font-awesome-icon :icon="['fas', 'pencil-alt']" />
        Edit Folder
      </b-button>
      <b-button
        v-if="canManagePermissions(folder)"
        :id="`${idTemplate}-managePermissions`"
        size="sm"
        variant="outline-primary"
        class="btn-folder-delete"
        :disabled="isDragModeEnabled"
        :to="{
          name: 'dataManagementPermissions',
          params: { itemId: folderUuid, type: permissionItemTypes.folder }
        }"
      >
        <font-awesome-icon :icon="['fas', 'key']" />
        Manage Permissions
      </b-button>
      <b-button
        v-if="canDelete(folder) && !isLoanCalculatorModule"
        :id="`${idTemplate}-deleteFolder`"
        size="sm"
        variant="outline-danger"
        :disabled="isDragModeEnabled"
        @click="deleteFolderFromList"
      >
        <font-awesome-icon :icon="['fas', 'trash']" />
        Delete Folder
      </b-button>
    </div>
  </div>
</template>

<script>
import moduleViewMixin from '@/core/mixins/module-view.mixin';
import { createNamespacedHelpers } from 'vuex';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');
const {
  mapState: sidebarMapState,
  mapActions: sidebarActions,
} = createNamespacedHelpers('sidebar');

export default {
  name: 'FolderHeader',
  mixins: [moduleViewMixin],
  props: {
    folder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      idTemplate: null,
      moduleUuid: null,
      folderUuid: null,
    };
  },
  computed: {
    ...dataManagementMapState({
      permissionTypes: state => state.permissionTypes,
      permissionItemTypes: state => state.permissionItemTypes,
    }),
    ...sidebarMapState({
      isDragModeEnabled: state => state.isDragModeEnabled,
    }),
    isLoanCalculatorModule() {
      return this.$route.path.includes('loan-calculator');
    },
  },
  methods: {
    ...dataManagementMapActions(['showFolderModal', 'deleteFolder']),
    ...sidebarActions(['getSidebarStructure', 'getModuleDetails']),
    async deleteFolderFromList() {
      const confirm = await this.$confirm(
        'Deleting folder...',
        `You are deleting folder "${this.folder.label}". Are you sure you want to continue?`,
      );
      if (confirm) {
        const { success } = await this.deleteFolder(this.folder);
        if (success) {
          this.$toasted.success(
            `Folder "${this.folder.label}" was deleted`, {
              duration: TOASTER_DURATION,
            });
          await this.updateData();
        } else {
          this.$toasted.error('Folder can not be deleted', {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
    showEditFolderModal() {
      this.showFolderModal(this.folder);
    },
    async updateData() {
      await this.getSidebarStructure();
      await this.getModuleDetails(this.moduleUuid);
    },
    createTable(isPowerApp, isPowerBiReport = false) {
      const currentKey = isPowerApp
        ? 'dataManagementBuilderPowerApps'
        : isPowerBiReport
          ? 'dataManagementBuilderPowerBiReport'
          : 'dataManagementBuilder';
      localStorage.setItem('table_rights', this.folder.currentUserPermissionLevel);
      this.$router.push({
        name: `${[currentKey]}.create`,
        query: { folderUuid: this.folderUuid }, params: { moduleUuid: this.moduleUuid },
      });
    },
  },
  mounted() {
    this.moduleUuid = this.folder.moduleGuid;
    this.folderUuid = this.folder.folderGuid;
    this.idTemplate = this.$options.filters.displayName(this.folder.label);
  },
};
</script>

<style scoped lang="scss">
.header--wrapper {
  margin-bottom: 20px;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  padding-top: 1rem;

  .header--wrapper__title {
    cursor: help;
    margin-bottom: 20px;
  }

  .actions--container {
    width: fit-content;
    margin-left: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
}

@media (min-width: 1200px) {
  .header--wrapper {
    display: flex;
    align-items: center;

    .header--wrapper__title {
      margin-bottom: 0;
    }
  }
}
</style>